import { useGetTechnicalMessageQuery } from '@api/technicalMessage.api';
import { TechnicalMessage } from '@api/types';
import { useAppDispatch, useAppSelector, useTechnicalMessageCount } from '@hooks';
import { getTableBlockData, initBlock, setFilters, setOffset } from '@store/table/table.slice';
import debounce from 'lodash/debounce';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ITEMS_PER_PAGE = 10;

type TechnicalMessageContextProps = {
  children: React.ReactNode;
  id: string;
  numberItemsOnPage?: number;
};

export type TechnicalMessageFilters = {
  Search: string;
  CreatedOn: string;
  MessageType: string;
  Attachments: string;
  Sorting: 'desc' | 'asc';
};

export type TechnicalMessageUpdateFiltersProps = {
  filterName: keyof TechnicalMessageFilters;
  value: TechnicalMessageFilters[keyof TechnicalMessageFilters];
};

type TechnicalMessageContextType = {
  messages: TechnicalMessage[];
  isWorking: boolean;
  isError: boolean;
  filters: TechnicalMessageUpdateFiltersProps[];
  updateFilters: ({ filterName, value }: TechnicalMessageUpdateFiltersProps) => void;
  updateSorting: (sortingValue: 'desc' | 'asc') => void;
  clearFilters: () => void;
  id: string;
  offset: number;
  totalCount: number;
  itemsPerPage: number;
  getFilterValue: (filterName: keyof TechnicalMessageFilters) => string | undefined;
};

export const TechnicalMessageContext = createContext<TechnicalMessageContextType | null>(null);

export const TechnicalMessageProvider = ({
  children,
  id,
  numberItemsOnPage = ITEMS_PER_PAGE,
}: TechnicalMessageContextProps) => {
  const dispatch = useAppDispatch();
  const { filters, offset } = useAppSelector(getTableBlockData(id)) as {
    filters: TechnicalMessageUpdateFiltersProps[];
    offset: number;
    visibleFilters: string[];
  };
  const initialFilters = useMemo(
    () => [
      {
        filterName: 'Search',
        value: '',
      },
      {
        filterName: 'CreatedOn',
        value: '',
      },
      {
        filterName: 'MessageType',
        value: '',
      },
      {
        filterName: 'Attachments',
        value: '',
      },
      {
        filterName: 'Sorting',
        value: 'desc',
      },
    ],
    []
  );
  const [currentFilters, setCurrentFilters] = useState(filters);

  const getFilterValue = useCallback(
    (filterName: keyof TechnicalMessageFilters) => {
      if (Array.isArray(filters)) {
        return filters.find((filter) => filter.filterName === filterName)?.value;
      }
      return '';
    },
    [filters]
  );

  const updateSorting = useCallback(
    (sortingValue: 'desc' | 'asc') => {
      dispatch(
        setFilters({
          id,
          filters: [
            ...filters.filter((filter) => filter.filterName !== 'Sorting'),
            {
              filterName: 'Sorting',
              value: sortingValue,
            },
          ],
        })
      );
    },
    [dispatch, filters, id]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateFilters = useCallback(
    debounce((newFilters) => {
      setCurrentFilters(newFilters);
    }, 800),
    []
  );

  const updateFilters = useCallback(
    ({
      filterName,
      value,
    }: {
      filterName: keyof TechnicalMessageFilters;
      value: TechnicalMessageFilters[keyof TechnicalMessageFilters];
    }) => {
      const newFilters = [
        {
          filterName,
          value,
        },
        ...filters?.filter((filter) => filter.filterName !== filterName),
      ];
      dispatch(setFilters({ id, filters: newFilters }));
      debouncedUpdateFilters(newFilters);
    },
    [dispatch, filters, id, debouncedUpdateFilters]
  );

  const fetchOptions = useMemo(() => {
    return {
      offset: offset || 0,
      limit: numberItemsOnPage,
      filters: currentFilters,
      sorting: {
        fieldToSort: 'CreatedOn',
        sortingDirection: getFilterValue('Sorting') || 'desc',
      },
    };
  }, [offset, numberItemsOnPage, currentFilters, getFilterValue]);

  const { currentData, isError, isFetching, isLoading } = useGetTechnicalMessageQuery(fetchOptions);

  const clearFilters = useCallback(() => {
    dispatch(setFilters({ id, filters: initialFilters }));
    dispatch(setOffset({ id, offset: 0 }));
  }, [dispatch, id, initialFilters]);

  useEffect(() => {
    dispatch(
      initBlock({
        id,
        filters: initialFilters,
        visibleFilters: [],
      })
    );
  }, [dispatch, id, initialFilters]);
  useTechnicalMessageCount();
  const totalCount = currentData?.data?.totalCount || 0;
  const isWorking = isLoading || isFetching;
  const messages = useMemo(
    () => currentData?.data?.technicalMessages || [],
    [currentData?.data?.technicalMessages]
  );

  const contextValue = useMemo(
    () => ({
      isWorking,
      isError,
      filters,
      id,
      offset,
      totalCount,
      itemsPerPage: numberItemsOnPage,
      updateFilters,
      clearFilters,
      messages,
      updateSorting,
      getFilterValue,
    }),
    [
      isWorking,
      isError,
      filters,
      id,
      offset,
      totalCount,
      numberItemsOnPage,
      updateFilters,
      clearFilters,
      messages,
      updateSorting,
      getFilterValue,
    ]
  );

  return (
    <TechnicalMessageContext.Provider value={contextValue}>
      {children}
    </TechnicalMessageContext.Provider>
  );
};

export const useTechnicalMessageContext = () => {
  const context = useContext(TechnicalMessageContext);

  if (!context) {
    throw new Error('useTechnicalMessage must be used within a TechnicalMessageProvider');
  }
  return context;
};
